// src/components/booking/AccommodationPlaceRow.jsx
import React, { useEffect, useState } from 'react';
import { 
  Star, 
  MapPin, 
  Heart,
  Eye,
  Calendar,
  MessageCircle,
  User,
  Users,
  Bed,
  Wifi,
  Car,
  Coffee,
  Waves,
  Award,
  CheckCircle,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { BookingModal } from './BookingModal';

const PRICING_TYPES = {
  'per_person': { icon: User, label: 'per person' },
  'per_two_people': { icon: Users, label: 'per 2 people' },
  'per_room': { icon: Bed, label: 'per room' },
  'per_night': { icon: Calendar, label: 'per night' }
};

const AMENITY_ICONS = {
  wifi: Wifi,
  parking: Car,
  breakfast: Coffee,
  pool: Waves,
  spa: Award
};

export const AccommodationPlaceRow = ({ 
  place, 
  customStyling, 
  bookSettings,
  searchCriteria,
  onViewDetails, 
  onBookPlace, 
  onInquire,
  className = ''
}) => {
  const [isFavorited, setIsFavorited] = useState(place.isFavorited || false);

  const [showBookingModal, setShowBookingModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const pricingType = place.pricingType || 'per_room';
  const pricing = PRICING_TYPES[pricingType];
  const PriceIcon = pricing?.icon || Bed;
  
  const images = place.images || [];
  const hasImages = images.length > 0;
  const primaryImage = place.images?.[0]?.url || place.imageUrl || place.thumbnail;
  const displayImage = hasImages
    ? (images[currentImageIndex]?.url || primaryImage)
    : primaryImage;
  
  const featuredAmenities = (place.amenities || [])
    .filter(amenity => AMENITY_ICONS[amenity.toLowerCase()])
    .slice(0, 3);

  const handleFavoriteToggle = (e) => {
    e.stopPropagation();
    setIsFavorited(!isFavorited);
  };

  const handleViewDetails = (e) => {
    e.stopPropagation();
    onViewDetails?.(place);
  };

  const handleInquire = (e) => {
    e.stopPropagation();
    onInquire?.(place);
  };

  const handleBookPlace = (e) => {
    e.stopPropagation();
    setShowBookingModal(true);
  };

  useEffect(() => {
    setCurrentImageIndex(0);
  }, [place.id, images.length]);

  const handlePrevImage = (e) => {
    e.stopPropagation();
    if (images.length < 2) return;
    setCurrentImageIndex(prev => (prev - 1 + images.length) % images.length);
  };

  const handleNextImage = (e) => {
    e.stopPropagation();
    if (images.length < 2) return;
    setCurrentImageIndex(prev => (prev + 1) % images.length);
  };

  // Add this before your return statement
  if (showBookingModal) {
    return (
      <BookingModal
        isOpen={showBookingModal}
        onClose={() => setShowBookingModal(false)}
        place={place}
        searchCriteria={searchCriteria} // Pass this from parent
        bookSettings={bookSettings} // Pass this from parent
        customStyling={customStyling}
      />
    );
  }

  return (
    <div className={`bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-all duration-200 group cursor-pointer ${className}`}>
      <div className="flex gap-4 p-4">
        {/* Image */}
        <div className="w-48 h-36 bg-gray-100 rounded-xl overflow-hidden flex-shrink-0 relative">
          {displayImage ? (
            <img
              src={displayImage}
              alt={place.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
              <Bed className="h-8 w-8 text-gray-400" />
            </div>
          )}

          {/* Favorite Button */}
          <button
            onClick={handleFavoriteToggle}
            className={`absolute top-2 right-2 p-1.5 rounded-full backdrop-blur-sm transition-all ${
              isFavorited 
                ? 'bg-red-500 text-white' 
                : 'bg-white/80 text-gray-600 hover:bg-white hover:text-red-500'
            }`}
          >
            <Heart className={`h-3 w-3 ${isFavorited ? 'fill-current' : ''}`} />
          </button>

          {/* Badges */}
          {place.isVerified && (
            <div className="absolute top-2 left-2 bg-emerald-500 text-white px-1.5 py-0.5 rounded text-xs">
              <CheckCircle className="h-3 w-3" />
            </div>
          )}

          {place.images && place.images.length > 1 && (
            <>
              <button
                onClick={handlePrevImage}
                className="absolute left-1 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-white/80 text-gray-700 hover:bg-white shadow-sm"
                title="Previous image"
              >
                <ChevronLeft className="h-3 w-3" />
              </button>
              <button
                onClick={handleNextImage}
                className="absolute right-1 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-white/80 text-gray-700 hover:bg-white shadow-sm"
                title="Next image"
              >
                <ChevronRight className="h-3 w-3" />
              </button>
              <div className="absolute bottom-2 right-2 bg-black/60 text-white px-2 py-0.5 rounded-full text-[10px] backdrop-blur-sm">
                {currentImageIndex + 1} / {place.images.length}
              </div>
            </>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 flex">
          {/* Main Content */}
          <div className="flex-1 pr-4">
            {/* Header */}
            <div className="flex items-start justify-between mb-2">
              <h3 className="font-bold text-gray-900 text-xl line-clamp-1 pr-2">
                {place.title}
              </h3>
              {place.rating && (
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Star className="h-4 w-4 text-yellow-400 fill-current" />
                  <span className="font-semibold text-gray-900">
                    {place.rating}
                  </span>
                  {place.reviewCount && (
                    <span className="text-gray-500 text-sm">
                      ({place.reviewCount})
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Location */}
            <div className="flex items-center gap-1 text-gray-600 mb-3">
              <MapPin className="h-4 w-4 flex-shrink-0" />
              <span className="text-sm">{place.location}</span>
            </div>

            {/* Description */}
            <p className="text-gray-600 text-sm line-clamp-2 mb-3 leading-relaxed">
              {place.description}
            </p>

            {/* Amenities */}
            {featuredAmenities.length > 0 && (
              <div className="flex items-center gap-4 mb-2">
                {featuredAmenities.map((amenity, index) => {
                  const AmenityIcon = AMENITY_ICONS[amenity.toLowerCase()];
                  return AmenityIcon ? (
                    <div
                      key={index}
                      className="flex items-center gap-1 text-gray-600 text-xs"
                    >
                      <AmenityIcon className="h-4 w-4" />
                      <span className="capitalize">{amenity}</span>
                    </div>
                  ) : null;
                })}
                {place.amenities && place.amenities.length > 3 && (
                  <span className="text-xs text-gray-500">
                    +{place.amenities.length - 3} more
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Price & Actions */}
          <div className="flex flex-col items-end justify-between">
            {/* Price */}
            <div className="text-right mb-4">
              <div className="flex items-center gap-2 mb-1">
                <PriceIcon className="h-4 w-4 text-gray-600" />
                <span className="font-bold text-gray-900 text-2xl">
                  R{place.price?.toLocaleString() || '0'}
                </span>
              </div>
              <div className="text-xs text-gray-600">
                {pricing?.label || 'per night'}
              </div>
            </div>

            {/* Action Icons */}
            <div className="flex items-center gap-2">
              {/* View Details */}
              <button
                onClick={handleViewDetails}
                className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                title="View Details"
              >
                <Eye className="h-4 w-4" />
              </button>

              {/* Inquire */}
              <button
                onClick={handleInquire}
                className="p-2 rounded-lg bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors"
                title="Send Inquiry"
              >
                <MessageCircle className="h-4 w-4" />
              </button>

              {/* Book Now */}
              <button
                onClick={handleBookPlace}
                className="p-2 rounded-lg text-white hover:opacity-90 transition-opacity"
                style={{ backgroundColor: customStyling?.primaryColor || '#10b981' }}
                title="Book Now"
              >
                <Calendar className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccommodationPlaceRow;
